import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { X, Search, Plus, Minus, ImageIcon, Lock, SlidersHorizontal, CalendarDays, ChevronDown } from 'lucide-react';
import { specialsAPI, catalogAPI } from '../../api';
import { Calendar } from '../../components/ui/calendar';
import { useAuth } from '../../context/AuthContext';
import { useCart } from '../../context/CartContext';
import AuthGate from '../AuthGate';
import Breadcrumbs from '../layout/Breadcrumbs';
import { toast } from 'sonner';
import { formatApiError } from '../../lib/apiError';

function SpecialRow({ item, qty, isHidden, onSetQty, onAdd, isEven }) {
  const [showPopup, setShowPopup] = useState(false);
  const [popupPos, setPopupPos] = useState({ top: 0, left: 0 });
  const thumbRef = useRef(null);

  const handleMouseEnter = () => {
    if (!item.image_url) return;
    const rect = thumbRef.current?.getBoundingClientRect();
    if (rect) {
      setPopupPos({ top: rect.top - 10, left: rect.right + 12 });
    }
    setShowPopup(true);
  };

  return (
    <>
      {/* Desktop row */}
      <div
        data-testid={`special-row-${item._id}`}
        className={`hidden md:grid md:grid-cols-[1fr_48px_140px_80px_120px_140px_80px] items-center px-4 py-3 border-b border-[#f0efed] transition-colors hover:bg-[#fbfaf8] ${isEven ? '' : 'bg-[#fafaf9]'}`}
      >
        {/* Product name */}
        <Link to={`/specials/${item._id}`} className="text-sm font-medium text-[#2d2d2d] hover:text-[#2f6b4f] transition-colors truncate pr-2 uppercase">
          {item.name}
        </Link>

        {/* Image thumbnail */}
        <div
          ref={thumbRef}
          className="flex justify-center"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={() => setShowPopup(false)}
        >
          {item.image_url ? (
            <img src={item.image_url} alt={item.name} className="w-8 h-8 object-cover rounded border border-[#e7e3dd] cursor-pointer" />
          ) : (
            <div className="w-8 h-8 bg-[#f5f4f2] rounded border border-[#e7e3dd] flex items-center justify-center">
              <ImageIcon size={12} className="text-[#c0c0c0]" />
            </div>
          )}
        </div>

        {/* Category */}
        <span className="text-xs text-[#6f6a63] truncate">{item.category || '-'}</span>

        {/* Stock */}
        <div className="text-center">
          {item.stock > 10 ? (
            <span className="text-xs text-[#2f6b4f] font-medium">{item.stock}</span>
          ) : item.stock > 0 ? (
            <span className="text-xs text-[#b28a4a] font-medium">{item.stock}</span>
          ) : (
            <span className="text-xs text-[#c0c0c0]">-</span>
          )}
        </div>

        {/* Price */}
        <div className="text-center">
          {isHidden ? (
            <Link to="/login" className="inline-flex items-center gap-1 text-[10px] text-[#2f6b4f] font-medium hover:underline">
              <Lock size={10} /> Log in
            </Link>
          ) : item.price > 0 ? (
            <span className="text-sm font-semibold text-[#2d2d2d]">${item.price.toFixed(2)}</span>
          ) : (
            <span className="text-xs text-[#c0c0c0]">-</span>
          )}
        </div>

        {/* Quantity */}
        <div className="flex items-center justify-center">
          {!isHidden && (
            <div className="flex items-center border border-[#e7e3dd] rounded">
              <button onClick={() => onSetQty(qty - 1)} className="px-1.5 py-1 hover:bg-gray-50 text-[#6f6a63]"><Minus size={12} /></button>
              <input
                data-testid={`special-qty-${item._id}`}
                type="number"
                value={qty}
                onChange={(e) => onSetQty(parseInt(e.target.value) || 1)}
                className="w-10 text-center text-xs border-x border-[#e7e3dd] py-1 focus:outline-none"
                min="1"
              />
              <button onClick={() => onSetQty(qty + 1)} className="px-1.5 py-1 hover:bg-gray-50 text-[#6f6a63]"><Plus size={12} /></button>
            </div>
          )}
        </div>

        {/* Add button */}
        <div className="flex justify-center">
          {!isHidden && item.stock > 0 && (
            <button
              data-testid={`special-add-btn-${item._id}`}
              onClick={onAdd}
              className="bg-[#2f3e36] text-white text-[10px] tracking-wider uppercase font-medium px-4 py-1.5 rounded hover:bg-[#253029] transition-colors active:scale-95"
            >
              Add
            </button>
          )}
          {!isHidden && item.stock !== undefined && item.stock <= 0 && (
            <span className="text-[10px] text-red-500 font-medium">Sold Out</span>
          )}
        </div>
      </div>

      {/* Mobile row */}
      <div className={`md:hidden px-4 py-3 border-b border-[#f0efed] ${isEven ? '' : 'bg-[#fafaf9]'}`}>
        <div className="flex items-center gap-3 mb-2">
          {item.image_url ? (
            <img src={item.image_url} alt={item.name} className="w-10 h-10 object-cover rounded border border-[#e7e3dd]" />
          ) : (
            <div className="w-10 h-10 bg-[#f5f4f2] rounded border border-[#e7e3dd] flex items-center justify-center">
              <ImageIcon size={14} className="text-[#c0c0c0]" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <Link to={`/specials/${item._id}`} className="text-sm font-medium text-[#2d2d2d] hover:text-[#2f6b4f] block truncate uppercase">{item.name}</Link>
            <span className="text-[10px] text-[#6f6a63]">{item.category}</span>
          </div>
        </div>
        {isHidden ? (
          <Link to="/login" className="inline-flex items-center gap-1 text-[11px] text-[#2f6b4f] font-medium hover:underline">
            <Lock size={11} /> Log in to see prices
          </Link>
        ) : (
          <div className="flex items-center justify-between gap-3">
            <span className="text-sm font-semibold">{item.price > 0 ? `$${item.price.toFixed(2)}` : '-'}</span>
            <div className="flex items-center gap-2">
              <div className="flex items-center border border-[#e7e3dd] rounded">
                <button onClick={() => onSetQty(qty - 1)} className="px-1.5 py-1"><Minus size={12} /></button>
                <input type="number" value={qty} onChange={(e) => onSetQty(parseInt(e.target.value) || 1)} className="w-10 text-center text-xs border-x border-[#e7e3dd] py-1 focus:outline-none" min="1" />
                <button onClick={() => onSetQty(qty + 1)} className="px-1.5 py-1"><Plus size={12} /></button>
              </div>
              {item.stock > 0 ? (
                <button onClick={onAdd} className="bg-[#2f3e36] text-white text-[10px] tracking-wider uppercase font-medium px-3 py-1.5 rounded hover:bg-[#253029] transition-colors">Add</button>
              ) : item.stock !== undefined && item.stock <= 0 ? (
                <span className="text-[10px] text-red-500">Sold Out</span>
              ) : (
                <button onClick={onAdd} className="bg-[#2f3e36] text-white text-[10px] tracking-wider uppercase font-medium px-3 py-1.5 rounded hover:bg-[#253029] transition-colors">Add</button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Image popup on hover */}
      {showPopup && item.image_url && (
        <div
          className="fixed z-[9999] pointer-events-none"
          style={{ top: `${popupPos.top}px`, left: `${popupPos.left}px`, transform: 'translateY(-50%)' }}
        >
          <div className="bg-white rounded-xl shadow-2xl border border-[#e7e3dd] overflow-hidden p-1">
            <img src={item.image_url} alt={item.name} className="w-64 h-64 object-cover rounded-lg" />
          </div>
        </div>
      )}
    </>
  );
}

export default function SpecialsPage({ mode = 'specials' }) {
  return (
    <AuthGate>
      <SpecialsPageContent mode={mode} />
    </AuthGate>
  );
}

function SpecialsPageContent({ mode }) {
  const isShopNow = mode === 'shopnow';
  const pageTitle = isShopNow ? 'Shop Now' : 'Weekly Specials';
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [colors, setColors] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const { user } = useAuth();
  const { addToCart } = useCart();

  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [categoryFilter, setCategoryFilter] = useState(searchParams.get('category') || '');
  const [colorFilter, setColorFilter] = useState(searchParams.get('color') || '');
  const [catSearch, setCatSearch] = useState('');
  const [colorSearch, setColorSearch] = useState('');
  const currentPage = parseInt(searchParams.get('page') || '1');

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page: currentPage, limit: 24 };
      if (search) params.search = search;
      if (categoryFilter) params.category = categoryFilter;
      if (colorFilter) params.color = colorFilter;
      const res = await specialsAPI.getSpecialsCatalog(params);
      setItems(res.data.items);
      setTotal(res.data.total);
      setCategories(res.data.categories || []);
      setColors(res.data.colors || []);
    } catch (err) {
      console.error('Failed to load specials', err);
    } finally {
      setLoading(false);
    }
  }, [search, categoryFilter, colorFilter, currentPage]);

  useEffect(() => { loadData(); }, [loadData]);

  const updateFilter = (key, value) => {
    const p = new URLSearchParams(searchParams);
    if (value) p.set(key, value); else p.delete(key);
    if (key !== 'page') p.set('page', '1');
    setSearchParams(p);
    window.scrollTo(0, 0);
    if (key === 'search') setSearch(value);
    if (key === 'category') setCategoryFilter(value);
    if (key === 'color') setColorFilter(value);
  };

  const clearFilters = () => {
    setSearch('');
    setCategoryFilter('');
    setColorFilter('');
    setSearchParams({});
  };

  const hasActiveFilters = search || categoryFilter || colorFilter;
  const filteredCats = catSearch ? categories.filter(c => c.toLowerCase().includes(catSearch.toLowerCase())) : categories;
  const filteredColors = colorSearch ? colors.filter(c => c.toLowerCase().includes(colorSearch.toLowerCase())) : colors;

  const breadcrumbs = [{ label: pageTitle }];

  // Per-row quantity state
  const [quantities, setQuantities] = useState({});
  const setQty = (id, val) => setQuantities(prev => ({ ...prev, [id]: Math.max(1, val) }));
  const getQty = (id) => quantities[id] || 1;

  // ── Holidays (non-working days) ──
  const [holidays, setHolidays] = useState([]);
  const holidaySet = new Set(holidays.map(h => h.date));

  useEffect(() => {
    catalogAPI.getHolidays().then(res => setHolidays(res.data.holidays || [])).catch(() => {});
  }, []);

  // ── Delivery date logic ──
  const toLocalDateStr = (d) => {
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };

  // Advance date to next valid business day (skip Sundays + holidays)
  const skipToNextBusinessDay = (d, hSet) => {
    let safe = 0;
    while (safe < 60) {
      const dayOfWeek = d.getDay();
      const ds = toLocalDateStr(d);
      if (dayOfWeek !== 0 && !hSet.has(ds)) break; // valid day
      d.setDate(d.getDate() + 1);
      safe++;
    }
    return d;
  };

  const getMinDeliveryDate = (hSet) => {
    const now = new Date();
    const day = now.getDay();
    const d = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    if (day >= 1 && day <= 4) { d.setDate(d.getDate() + 1); }
    else if (day === 5) { d.setDate(d.getDate() + 3); }
    else if (day === 6) { d.setDate(d.getDate() + 3); }
    else { d.setDate(d.getDate() + 2); }
    skipToNextBusinessDay(d, hSet);
    return toLocalDateStr(d);
  };

  const minDate = getMinDeliveryDate(holidaySet);
  const [deliveryDate, setDeliveryDate] = useState('');
  const [calOpen, setCalOpen] = useState(false);
  const todayDay = new Date().getDay();
  const isWeekendOrder = (todayDay === 0 || todayDay === 6);

  // Update deliveryDate when minDate or holidays change
  useEffect(() => {
    const newMin = getMinDeliveryDate(holidaySet);
    setDeliveryDate(prev => (!prev || prev < newMin) ? newMin : prev);
  }, [holidays]); // eslint-disable-line react-hooks/exhaustive-deps

  // Initialize on mount
  useEffect(() => {
    setDeliveryDate(minDate);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const formatDisplayDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr + 'T12:00:00');
    return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
  };

  const handleDeliveryDateChange = (val) => {
    if (val < minDate) {
      toast.error('Please select a valid delivery date');
      return;
    }
    const selected = new Date(val + 'T12:00:00');
    if (selected.getDay() === 0) {
      toast.error('Sunday is not available as a delivery date. Please select Monday through Saturday.');
      return;
    }
    if (holidaySet.has(val)) {
      const label = holidays.find(h => h.date === val)?.label;
      toast.error(`${formatDisplayDate(val)}${label ? ` (${label})` : ''} is a non-working day. Please select another date.`);
      return;
    }
    setDeliveryDate(val);
  };

  const handleAddRow = async (itemId) => {
    if (!user) return;
    if (!deliveryDate) {
      toast.error('Please select a delivery date');
      return;
    }
    const qty = getQty(itemId);
    try {
      await addToCart(itemId, qty, 'specials', deliveryDate);
      toast.success('Added to cart!');
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to add'));
    }
  };

  return (
    <div className="pw-container py-2">
      <Breadcrumbs items={breadcrumbs} />

      <div className="flex gap-8">
        {/* Sidebar Filters */}
        <aside className="hidden lg:block w-56 flex-shrink-0">
          <div className="sticky top-28">
            <h3 className="text-sm font-semibold tracking-wider uppercase mb-4">Filters</h3>

            {/* Search */}
            <div className="mb-5">
              <label className="text-[10px] font-semibold tracking-wider uppercase text-[#6f6a63] block mb-2">Search</label>
              <div className="relative">
                <input
                  data-testid="specials-search-input"
                  type="text"
                  value={search}
                  onChange={(e) => updateFilter('search', e.target.value)}
                  placeholder="Search products..."
                  className="w-full pl-8 pr-3 py-2 border border-[#e7e3dd] rounded-lg text-xs bg-white focus:outline-none focus:border-[#2f6b4f]"
                />
                <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#6f6a63]" />
              </div>
            </div>

            {/* Category */}
            <div className="mb-6">
              <h4 className="text-xs font-medium tracking-wider uppercase text-[#6f6a63] mb-2">Category</h4>
              <input
                type="text"
                value={catSearch}
                onChange={(e) => setCatSearch(e.target.value)}
                placeholder="Filter categories..."
                className="w-full px-3 py-1.5 border border-[#e7e3dd] rounded-lg text-[10px] mb-2 focus:outline-none focus:border-[#2f6b4f]"
                style={{ fontSize: '16px', touchAction: 'manipulation' }}
              />
              <div className="space-y-1 max-h-48 overflow-y-auto" style={{ WebkitOverflowScrolling: 'touch', overscrollBehavior: 'contain', touchAction: 'pan-y' }}>
                <button
                  onClick={() => updateFilter('category', '')}
                  className={`block w-full text-left text-sm py-1 px-2 rounded transition-colors ${!categoryFilter ? 'bg-[#e7f1ea] text-[#2f6b4f] font-medium' : 'text-[#6f6a63] hover:text-[#2d2d2d]'}`}
                >All</button>
                {filteredCats.map(cat => (
                  <button
                    key={cat}
                    onClick={() => updateFilter('category', categoryFilter === cat ? '' : cat)}
                    className={`block w-full text-left text-sm py-1 px-2 rounded transition-colors ${categoryFilter === cat ? 'bg-[#e7f1ea] text-[#2f6b4f] font-medium' : 'text-[#6f6a63] hover:text-[#2d2d2d]'}`}
                  >{cat}</button>
                ))}
              </div>
            </div>

            {/* Color */}
            {colors.length > 0 && (
              <div className="mb-6">
                <h4 className="text-xs font-medium tracking-wider uppercase text-[#6f6a63] mb-2">Color</h4>
                <input
                  type="text"
                  value={colorSearch}
                  onChange={(e) => setColorSearch(e.target.value)}
                  placeholder="Filter colors..."
                  className="w-full px-3 py-1.5 border border-[#e7e3dd] rounded-lg text-[10px] mb-2 focus:outline-none focus:border-[#2f6b4f]"
                  style={{ fontSize: '16px', touchAction: 'manipulation' }}
                />
                <div className="space-y-1 max-h-40 overflow-y-auto" style={{ WebkitOverflowScrolling: 'touch', overscrollBehavior: 'contain', touchAction: 'pan-y' }}>
                  <button
                    onClick={() => updateFilter('color', '')}
                    className={`block w-full text-left text-sm py-1 px-2 rounded transition-colors ${!colorFilter ? 'bg-[#e7f1ea] text-[#2f6b4f] font-medium' : 'text-[#6f6a63] hover:text-[#2d2d2d]'}`}
                  >All</button>
                  {filteredColors.map(c => (
                    <button
                      key={c}
                      onClick={() => updateFilter('color', colorFilter === c ? '' : c)}
                      className={`block w-full text-left text-sm py-1 px-2 rounded transition-colors ${colorFilter === c ? 'bg-[#e7f1ea] text-[#2f6b4f] font-medium' : 'text-[#6f6a63] hover:text-[#2d2d2d]'}`}
                    >{c}</button>
                  ))}
                </div>
              </div>
            )}

            {hasActiveFilters && (
              <button onClick={clearFilters} className="text-xs text-red-600 hover:text-red-700 underline">Clear all filters</button>
            )}
          </div>
        </aside>

        {/* Products Table */}
        <div className="flex-1 min-w-0">
          {/* Top Bar */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <h1 data-testid="specials-page-title" className={`text-lg md:text-xl font-semibold tracking-wider uppercase ${isShopNow ? 'animate-pulse-subtle' : ''}`}>{pageTitle}</h1>
              <span className="text-sm text-[#6f6a63]">({total} products)</span>
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="lg:hidden flex items-center gap-1.5 px-3 py-2 border border-[#e7e3dd] rounded-lg text-sm"
            >
              <SlidersHorizontal size={14} /> Filters
            </button>
          </div>

          {/* Active Filters */}
          {hasActiveFilters && (
            <div className="flex flex-wrap gap-2 mb-4">
              {categoryFilter && (
                <span className="flex items-center gap-1 bg-[#e7f1ea] text-[#2f6b4f] text-xs px-3 py-1 rounded-full">
                  {categoryFilter} <X size={12} className="cursor-pointer" onClick={() => updateFilter('category', '')} />
                </span>
              )}
              {colorFilter && (
                <span className="flex items-center gap-1 bg-[#e7f1ea] text-[#2f6b4f] text-xs px-3 py-1 rounded-full">
                  {colorFilter} <X size={12} className="cursor-pointer" onClick={() => updateFilter('color', '')} />
                </span>
              )}
              {search && (
                <span className="flex items-center gap-1 bg-[#e7f1ea] text-[#2f6b4f] text-xs px-3 py-1 rounded-full">
                  "{search}" <X size={12} className="cursor-pointer" onClick={() => updateFilter('search', '')} />
                </span>
              )}
            </div>
          )}

          {/* Delivery Date Selector */}
          {user && (
            <div data-testid="delivery-date-section" className="bg-white border border-[#e7e3dd] rounded-xl px-5 py-4 mb-6">
              <div className="flex items-center gap-5 flex-wrap">
                <div className="flex items-center gap-2">
                  <CalendarDays size={18} className="text-[#2f6b4f]" />
                  <span className="text-sm font-semibold text-[#2d2d2d]">Delivery Date</span>
                </div>
                <button
                  data-testid="delivery-date-toggle"
                  onClick={() => setCalOpen(!calOpen)}
                  className="flex items-center gap-2 px-3 py-2 border border-[#e7e3dd] rounded-lg text-sm hover:border-[#2f6b4f] transition-colors cursor-pointer"
                >
                  <span className="font-medium text-[#2d2d2d]">{formatDisplayDate(deliveryDate)}</span>
                  <ChevronDown size={14} className={`text-[#6f6a63] transition-transform ${calOpen ? 'rotate-180' : ''}`} />
                </button>
                {holidays.length > 0 && (
                  <span className="text-[10px] text-[#6f6a63] flex items-center gap-1">
                    <span className="inline-block w-2.5 h-2.5 rounded-sm bg-[#2f6b4f]"></span> Non-working day
                  </span>
                )}
              </div>
              {calOpen && (
                <div className="mt-3">
                  <Calendar
                    mode="single"
                    selected={deliveryDate ? new Date(deliveryDate + 'T12:00:00') : undefined}
                    onSelect={(date) => {
                      if (date) {
                        const val = toLocalDateStr(date);
                        handleDeliveryDateChange(val);
                        setCalOpen(false);
                      }
                    }}
                    disabled={(date) => {
                      const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
                      const minD = new Date(minDate + 'T00:00:00');
                      if (d < minD) return true;
                      if (d.getDay() === 0) return true;
                      return false;
                    }}
                    modifiers={{ holiday: holidays.map(h => new Date(h.date + 'T12:00:00')) }}
                    modifiersStyles={{ holiday: { backgroundColor: '#2f6b4f', color: '#ffffff', borderRadius: '6px', fontWeight: '600' } }}
                    fromDate={new Date(minDate + 'T00:00:00')}
                    className="rounded-lg border border-[#e7e3dd] inline-block"
                  />
                </div>
              )}
              <p className="text-[11px] text-[#6f6a63] mt-3">
                Delivery is scheduled for the <strong>next business day</strong> (Mon–Sat). You can place orders any day of the week.
                {isWeekendOrder && <span className="text-[#b28a4a] font-medium ml-1">You are ordering on a weekend — your order will be processed Monday and delivered Tuesday.</span>}
              </p>
            </div>
          )}

          {/* Table */}
          {loading ? (
            <div className="space-y-3">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="animate-pulse bg-[#fbfaf8] rounded-lg h-14" />
              ))}
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-[#6f6a63] mb-4">No products found.</p>
              <button onClick={clearFilters} className="text-[#2f6b4f] underline text-sm">Clear filters</button>
            </div>
          ) : (
            <>
              <div className="bg-white border border-[#e7e3dd] rounded-xl overflow-hidden">
                {/* Table Header */}
                <div className="hidden md:grid md:grid-cols-[1fr_48px_140px_80px_120px_140px_80px] items-center bg-[#fbfaf8] border-b border-[#e7e3dd] px-4 py-2.5">
                  <span className="text-[10px] font-semibold tracking-wider uppercase text-[#6f6a63]">Product</span>
                  <span className="text-[10px] font-semibold tracking-wider uppercase text-[#6f6a63] text-center">Image</span>
                  <span className="text-[10px] font-semibold tracking-wider uppercase text-[#6f6a63]">Category</span>
                  <span className="text-[10px] font-semibold tracking-wider uppercase text-[#6f6a63] text-center">Stock</span>
                  <span className="text-[10px] font-semibold tracking-wider uppercase text-[#6f6a63] text-center">{user ? 'Unit Price' : ''}</span>
                  <span className="text-[10px] font-semibold tracking-wider uppercase text-[#6f6a63] text-center">{user ? 'Qty' : ''}</span>
                  <span className="text-[10px] font-semibold tracking-wider uppercase text-[#6f6a63] text-center"></span>
                </div>

                {/* Rows */}
                {items.map((item, idx) => {
                  const qty = getQty(item._id);
                  const isHidden = !user || item.prices_hidden;
                  return (
                    <SpecialRow
                      key={item._id}
                      item={item}
                      qty={qty}
                      isHidden={isHidden}
                      onSetQty={(val) => setQty(item._id, val)}
                      onAdd={() => handleAddRow(item._id)}
                      isEven={idx % 2 === 0}
                    />
                  );
                })}
              </div>

              {/* Pagination */}
              {total > 24 && (
                <div className="flex justify-center gap-2 mt-8">
                  {Array.from({ length: Math.ceil(total / 24) }, (_, i) => i + 1).map((p) => (
                    <button
                      key={p}
                      onClick={() => { const sp = new URLSearchParams(searchParams); sp.set('page', String(p)); setSearchParams(sp); }}
                      className={`w-8 h-8 rounded text-sm ${currentPage === p ? 'bg-[#2d2d2d] text-white' : 'border border-[#e7e3dd] hover:bg-gray-50'}`}
                    >{p}</button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Mobile Filters Overlay */}
      {showFilters && (
        <div className="lg:hidden fixed inset-0 z-50 bg-black/50" onClick={() => setShowFilters(false)}>
          <div className="absolute right-0 top-0 bottom-0 w-72 bg-white p-6 overflow-y-auto" style={{ WebkitOverflowScrolling: 'touch', overscrollBehavior: 'contain' }} onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-sm font-semibold tracking-wider uppercase">Filters</h3>
              <button onClick={() => setShowFilters(false)}><X size={20} /></button>
            </div>
            <div className="mb-6">
              <h4 className="text-xs font-medium tracking-wider uppercase text-[#6f6a63] mb-2">Category</h4>
              {categories.map(cat => (
                <button key={cat} onClick={() => { updateFilter('category', categoryFilter === cat ? '' : cat); setShowFilters(false); }} className={`block w-full text-left text-sm py-1.5 ${categoryFilter === cat ? 'text-[#2f6b4f] font-medium' : 'text-[#6f6a63]'}`}>
                  {cat}
                </button>
              ))}
            </div>
            {colors.length > 0 && (
              <div className="mb-6">
                <h4 className="text-xs font-medium tracking-wider uppercase text-[#6f6a63] mb-2">Color</h4>
                {colors.map(c => (
                  <button key={c} onClick={() => { updateFilter('color', colorFilter === c ? '' : c); setShowFilters(false); }} className={`block w-full text-left text-sm py-1.5 ${colorFilter === c ? 'text-[#2f6b4f] font-medium' : 'text-[#6f6a63]'}`}>
                    {c}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
